import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Clock, CheckCircle, AlertTriangle, ArrowRight } from 'lucide-react';
import { portalApi } from '@/services/portal-api';
import { StatusBadge } from '@/components/portal/StatusBadge';

interface ApreciacaoItem {
  compartilhamentoId: string;
  status: string;
  dataCompartilhamento: string;
  totalAnotacoes: number;
  apreciacao: {
    id: string;
    numero: string;
    titulo: string;
    revisao: number;
    dataEmissao: string;
    maquina: { id: string; nome: string; tipo: string };
    projeto: { id: string; nome: string };
  };
}

export function PortalDashboard() {
  const [apreciacoes, setApreciacoes] = useState<ApreciacaoItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    portalApi.listarApreciacoes().then(res => {
      if (res.success && res.data) {
        setApreciacoes(res.data);
      }
      setLoading(false);
    });
  }, []);

  const pendentes = apreciacoes.filter(a => a.status === 'pendente');
  const aprovadas = apreciacoes.filter(a => a.status === 'aprovado');
  const revisao = apreciacoes.filter(a => a.status === 'revisao_solicitada');

  const cards = [
    { label: 'Pendentes', count: pendentes.length, icon: Clock, color: 'bg-yellow-50 text-yellow-700 border-yellow-200', iconColor: 'text-yellow-500' },
    { label: 'Aprovadas', count: aprovadas.length, icon: CheckCircle, color: 'bg-green-50 text-green-700 border-green-200', iconColor: 'text-green-500' },
    { label: 'Revisao Solicitada', count: revisao.length, icon: AlertTriangle, color: 'bg-red-50 text-red-700 border-red-200', iconColor: 'text-red-500' },
    { label: 'Total', count: apreciacoes.length, icon: FileText, color: 'bg-blue-50 text-blue-700 border-blue-200', iconColor: 'text-blue-500' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Portal do Cliente</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Acompanhe e revise as apreciacoes de risco compartilhadas com voce
        </p>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map(card => {
          const Icon = card.icon;
          return (
            <div key={card.label} className={`rounded-xl border p-4 ${card.color}`}>
              <div className="flex items-center justify-between mb-2">
                <Icon className={`w-5 h-5 ${card.iconColor}`} />
                <span className="text-2xl font-bold">{card.count}</span>
              </div>
              <span className="text-sm font-medium">{card.label}</span>
            </div>
          );
        })}
      </div>

      {/* Pendentes (priority list) */}
      {pendentes.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Aguardando sua revisao</h2>
            <Link to="/portal/apreciacoes" className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
              Ver todas <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="space-y-3">
            {pendentes.slice(0, 5).map(item => (
              <Link
                key={item.compartilhamentoId}
                to={`/portal/apreciacoes/${item.apreciacao.id}`}
                className="block bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-gray-500 dark:text-gray-400">{item.apreciacao.numero}</span>
                      <StatusBadge status={item.status} />
                    </div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {item.apreciacao.titulo}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {item.apreciacao.maquina.nome} | Rev. {item.apreciacao.revisao}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {apreciacoes.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="text-lg">Nenhuma apreciacao compartilhada</p>
          <p className="text-sm mt-1">Quando a equipe compartilhar documentos, eles aparecerao aqui.</p>
        </div>
      )}
    </div>
  );
}
